import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Box,
  Paper,
  MenuItem,
  Select,
  Button,
  FormHelperText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ─── Inject global styles ─────────────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap');

    :root {
      --navy:      #0B1F40;
      --navy-mid:  #132d5a;
      --gold:      #C9A84C;
      --gold-lt:   #e8c96e;
      --offwhite:  #F7F5F0;
      --surface:   #FFFFFF;
      --border:    #E2DDD4;
      --muted:     #7A7267;
      --text:      #1A1714;
      --error:     #C0392B;
      --success:   #1E7A4E;
      --radius:    10px;
      --shadow:    0 2px 16px rgba(11,31,64,.08), 0 1px 4px rgba(11,31,64,.06);
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--offwhite);
      font-family: 'DM Sans', sans-serif;
      color: var(--text);
    }

    .eq-page {
      min-height: 100vh;
      background:
        radial-gradient(ellipse 70% 45% at 50% -10%, rgba(201,168,76,.12) 0%, transparent 70%),
        var(--offwhite);
      padding: 48px 16px 80px;
    }

    .eq-masthead {
      max-width: 760px;
      margin: 0 auto 36px;
      text-align: center;
    }
    .eq-masthead-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--navy);
      color: var(--gold);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      padding: 6px 16px;
      border-radius: 100px;
      margin-bottom: 20px;
    }
    .eq-masthead-badge::before {
      content: '';
      width: 6px; height: 6px;
      background: var(--gold);
      border-radius: 50%;
    }
    .eq-masthead h1 {
      font-family: 'DM Serif Display', serif;
      font-size: clamp(28px, 5vw, 42px);
      font-weight: 400;
      color: var(--navy);
      line-height: 1.15;
      margin: 0 0 12px;
    }
    .eq-masthead p {
      font-size: 15px;
      color: var(--muted);
      font-weight: 300;
      margin: 0;
      line-height: 1.6;
    }

    .eq-card {
      max-width: 760px;
      margin: 0 auto;
      background: var(--surface);
      border-radius: 16px;
      box-shadow: var(--shadow);
      border: 1px solid var(--border);
      overflow: hidden;
    }

    .eq-section {
      padding: 28px 36px;
      border-bottom: 1px solid var(--border);
    }
    .eq-section:last-child { border-bottom: none; }

    .eq-section-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .12em;
      text-transform: uppercase;
      color: var(--gold);
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .eq-section-label::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(90deg, var(--border) 0%, transparent 100%);
    }

    .eq-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .04em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 6px;
    }
    .eq-label .req {
      color: var(--error);
      margin-left: 2px;
    }

    .eq-card .MuiOutlinedInput-root {
      border-radius: var(--radius) !important;
      background: #FAFAF8;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
    }
    .eq-card .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
      border-color: var(--border) !important;
    }
    .eq-card .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--gold) !important;
    }
    .eq-card .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--navy) !important;
      border-width: 2px !important;
    }
    .eq-card .MuiSelect-select {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
    }

    .eq-btn-save {
      background: var(--navy) !important;
      color: #fff !important;
      font-family: 'DM Sans', sans-serif !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      letter-spacing: .06em !important;
      text-transform: uppercase !important;
      padding: 12px 32px !important;
      border-radius: var(--radius) !important;
      box-shadow: 0 4px 14px rgba(11,31,64,.25) !important;
      transition: all .2s ease !important;
    }
    .eq-btn-save:hover {
      background: var(--navy-mid) !important;
      box-shadow: 0 6px 20px rgba(11,31,64,.35) !important;
      transform: translateY(-1px);
    }
    .eq-btn-reset {
      background: transparent !important;
      color: var(--muted) !important;
      font-family: 'DM Sans', sans-serif !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      letter-spacing: .06em !important;
      text-transform: uppercase !important;
      padding: 12px 28px !important;
      border-radius: var(--radius) !important;
      border: 1.5px solid var(--border) !important;
      transition: all .2s ease !important;
    }
    .eq-btn-reset:hover {
      border-color: var(--error) !important;
      color: var(--error) !important;
      background: #fdf5f5 !important;
    }

    .eq-footer-strip {
      background: #FAFAF8;
      border-top: 1px solid var(--border);
      padding: 20px 36px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
    }
    .eq-footer-note {
      font-size: 12px;
      color: var(--muted);
      font-weight: 300;
    }
    .eq-btn-row { display: flex; gap: 12px; flex-wrap: wrap; }

    .eq-dialog-header {
      background: var(--navy);
      padding: 14px 24px;
    }
    .eq-dialog-header .MuiTypography-root {
      color: #fff;
      font-family: 'DM Serif Display', serif !important;
      font-weight: 400;
      font-size: 18px;
    }

    /* ── School Header ── */
    .eq-school-header {
      max-width: 760px;
      margin: 0 auto 24px;
      background: var(--surface);
      border-radius: 16px;
      border: 1px solid var(--border);
      box-shadow: var(--shadow);
      padding: 20px 28px;
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .eq-school-logo {
      width: 72px;
      height: 72px;
      object-fit: contain;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: #f5f5f3;
      flex-shrink: 0;
    }
    .eq-school-logo-placeholder {
      width: 72px;
      height: 72px;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: var(--navy);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 22px;
      color: var(--gold);
      font-family: 'DM Serif Display', serif;
    }
    .eq-school-info { flex: 1; min-width: 0; }
    .eq-school-name {
      font-family: 'DM Serif Display', serif;
      font-size: clamp(16px, 3vw, 22px);
      color: var(--navy);
      font-weight: 400;
      margin: 0 0 2px;
      line-height: 1.2;
    }
    .eq-school-branch {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: var(--gold);
      margin: 0 0 8px;
    }
    .eq-school-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 18px;
    }
    .eq-school-meta-item {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      color: var(--muted);
      font-weight: 400;
    }
    .eq-school-meta-item svg {
      flex-shrink: 0;
      opacity: .7;
    }
    .eq-school-header-loading {
      max-width: 760px;
      margin: 0 auto 24px;
      background: var(--surface);
      border-radius: 16px;
      border: 1px solid var(--border);
      padding: 20px 28px;
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .eq-skeleton {
      background: linear-gradient(90deg, #ece9e3 25%, #f5f3ee 50%, #ece9e3 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
      border-radius: 6px;
    }
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ── Registration badge strip ── */
    .eq-reg-badge {
      max-width: 760px;
      margin: 0 auto 16px;
      text-align: center;
    }
    .eq-reg-badge-pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--navy);
      color: var(--gold);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      padding: 6px 16px;
      border-radius: 100px;
    }
    .eq-reg-badge-pill::before {
      content: '';
      width: 6px; height: 6px;
      background: var(--gold);
      border-radius: 50%;
    }

    @media (max-width: 600px) {
      .eq-section { padding: 20px 18px; }
      .eq-footer-strip { padding: 16px 18px; }
      .eq-btn-row { width: 100%; }
      .eq-btn-save, .eq-btn-reset { flex: 1; justify-content: center; }
      .eq-school-header { flex-direction: column; align-items: flex-start; padding: 16px 18px; gap: 14px; }
    }
  `}</style>
);

// ─── Helper: format YYYY-MM-DD → DD/MM/YYYY ───────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

function StudentRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [missingFields, setMissingFields] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [genders, setGenders] = useState([]);
  const [classes, setClasses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState({
    studentName: "",
    dob: "",
    gender: "",
    classId: "",
    contactNo: "",
    parentAndGuardian: "",
    motherName: "",
    emailId: "",
    address: "",
    pinCode: "",
    country: "",
    state: "",
    city: "",
    aadharNo: "",
    alternateMobile: "",
    nationality: "",
    prospectus: "",
  });
  const [mandatoryFieldsError, setMandatoryFieldsError] = useState(false);

  const fetchSchoolInfo = async () => {
    try {
      setSchoolLoading(true);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: "",
        }
      );
      if (!response.ok) throw new Error(`School fetch failed: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setSchoolInfo(data[0]);
      }
    } catch (error) {
      console.error("Error fetching school info:", error);
    } finally {
      setSchoolLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const apiUrl = "https://wsmsv1.aidigitalsec.com";
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };
      const fetchData = async (endpoint, name) => {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "POST",
          headers,
          body: JSON.stringify({}),
        });
        if (!response.ok) throw new Error(`${name} fetch failed: ${response.status}`);
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      };
      const [genderData, classData, countryData] = await Promise.all([
        fetchData("/api/Admission/ddlGender", "Gender"),
        fetchData("/api/Enquiry/ddlClassName", "Class"),
        fetchData("/api/Registration/ddlCountry", "Country"),
      ]);
      setGenders(genderData);
      setClasses(classData);
      setCountries(countryData);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      setSnackbarMessage("Failed to load dropdown data.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const fetchState = async (countryId) => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Registration/ddlState",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ countryId }),
        }
      );
      const data = await response.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
    }
  };

  const fetchCity = async (stateId) => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Registration/ddlCity",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ stateId }),
        }
      );
      const data = await response.json();
      setCities(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const mandatoryFields = [
      { name: 'Student Name', value: registrationData.studentName },
      { name: 'Date of Birth', value: registrationData.dob },
      { name: 'Gender', value: registrationData.gender },
      { name: 'Class', value: registrationData.classId },
      { name: 'Contact No.', value: registrationData.contactNo },
      { name: 'Father/Guardian Name', value: registrationData.parentAndGuardian },
    ];

    const missing = mandatoryFields.filter(field => !field.value);
    setMissingFields(missing.map(field => field.name));
    if (missing.length > 0) {
      setMandatoryFieldsError(true);
      return;
    }

    try {
      setIsLoading(true);

      // ── Fix 1: Format DOB from YYYY-MM-DD → DD/MM/YYYY (backend requirement) ──
      const formattedDOB = formatDate(registrationData.dob);

      // ── Fix 2: Format today's date the same way ────────────────────────────────
      const today = new Date().toISOString().split('T')[0];
      const formattedDated = formatDate(today);

      const payload = {
        registrationNo: "New",
        dated: formattedDated,                                    // ✅ fixed
        enquiryStatus: 0,
        enquiryID: 0,
        prostetusNo: registrationData.prospectus || "N/A",
        regFees: 0,
        classID: registrationData.classId || 0,
        name: registrationData.studentName || "",
        fatherName: registrationData.parentAndGuardian || "",
        dob: formattedDOB,                                        // ✅ fixed
        age: 0,
        gender: parseInt(registrationData.gender) || 0,          // ✅ fix 3: ensure integer
        nationality: registrationData.nationality || "",
        pinCode: registrationData.pinCode || "",
        alternateNo: registrationData.alternateMobile || "",
        mobileNo: registrationData.contactNo || "",
        email: registrationData.emailId || "",
        address: registrationData.address || "",
        motherName: registrationData.motherName || "",
        grandFather: "",
        adharNo: registrationData.aadharNo || "000000000000",
        countryId: registrationData.country || 0,
        stateId: registrationData.state || 0,
        cityID: registrationData.city || 0,
      };

      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Registration",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting registration:", error);
      setSnackbarMessage('Failed to submit registration. Please try again later.');
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setRegistrationData({
      studentName: "",
      dob: "",
      gender: "",
      classId: "",
      contactNo: "",
      parentAndGuardian: "",
      motherName: "",
      emailId: "",
      address: "",
      pinCode: "",
      country: "",
      state: "",
      city: "",
      aadharNo: "",
      alternateMobile: "",
      nationality: "",
      prospectus: "",
    });
    setMandatoryFieldsError(false);
  };

  useEffect(() => {
    fetchSchoolInfo();
    fetchDropdownData();
  }, []);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: '#FAFAF8',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '14px',
    },
  };

  return (
    <>
      <GlobalStyles />
      <div className="eq-page">

        {/* ── School Header ── */}
        {schoolLoading ? (
          <div className="eq-school-header-loading">
            <div className="eq-skeleton" style={{ width: 72, height: 72, borderRadius: 10, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="eq-skeleton" style={{ height: 20, width: '60%', marginBottom: 8 }} />
              <div className="eq-skeleton" style={{ height: 13, width: '40%', marginBottom: 10 }} />
              <div className="eq-skeleton" style={{ height: 12, width: '75%' }} />
            </div>
          </div>
        ) : schoolInfo ? (
          <div className="eq-school-header">
            {schoolInfo.logoImgUrl ? (
              <img
                src={schoolInfo.logoImgUrl}
                alt={schoolInfo.schoolName}
                className="eq-school-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="eq-school-logo-placeholder">
                {schoolInfo.schoolName ? schoolInfo.schoolName.charAt(0) : "S"}
              </div>
            )}
            <div className="eq-school-info">
              <p className="eq-school-name">{schoolInfo.schoolName}</p>
              {schoolInfo.branchName && schoolInfo.branchName !== schoolInfo.schoolName && (
                <p className="eq-school-branch">{schoolInfo.branchName}</p>
              )}
              <div className="eq-school-meta">
                {schoolInfo.address && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    {schoolInfo.address}
                  </span>
                )}
                {schoolInfo.phoneNo && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.83a16 16 0 0 0 6.29 6.29l1.62-1.62a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.07z"/>
                    </svg>
                    {schoolInfo.phoneNo}
                  </span>
                )}
                {schoolInfo.affilationNo && (
                  <span className="eq-school-meta-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                    Affil. No: {schoolInfo.affilationNo}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Registration badge ── */}
        <div className="eq-reg-badge">
          <span className="eq-reg-badge-pill">Student Registration</span>
        </div>
        <div className="eq-card">
          <form onSubmit={handleSubmit}>

            {/* ── Section 1: Personal Details ── */}
            <div className="eq-section">
              <div className="eq-section-label">Personal Details</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="studentName">
                    Student Name <span className="req">*</span>
                  </label>
                  <TextField id="studentName" type="text" fullWidth size="small"
                    value={registrationData.studentName}
                    onChange={(e) => setRegistrationData({ ...registrationData, studentName: e.target.value })}
                    placeholder="Full name of the student"
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !registrationData.studentName && (
                    <FormHelperText error>Please enter student name</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="dob">
                    Date of Birth <span className="req">*</span>
                  </label>
                  <TextField id="dob" type="date" fullWidth size="small"
                    value={registrationData.dob}
                    onChange={(e) => setRegistrationData({ ...registrationData, dob: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !registrationData.dob && (
                    <FormHelperText error>Please select date of birth</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="gender">
                    Gender <span className="req">*</span>
                  </label>
                  <Select id="gender" fullWidth size="small" displayEmpty
                    value={registrationData.gender}
                    onChange={(e) => setRegistrationData({ ...registrationData, gender: e.target.value })}
                    sx={fieldSx}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select Gender —</em></MenuItem>
                    {Array.isArray(genders) && genders.length > 0 ? (
                      genders.map((g) => (
                        <MenuItem key={g.genderId} value={g.genderId}>{g.gender}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No options available</em></MenuItem>
                    )}
                  </Select>
                  {mandatoryFieldsError && !registrationData.gender && (
                    <FormHelperText error>Please select gender</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="classId">
                    Applying for Class <span className="req">*</span>
                  </label>
                  <Select id="classId" fullWidth size="small" displayEmpty
                    value={registrationData.classId}
                    onChange={(e) => setRegistrationData({ ...registrationData, classId: e.target.value })}
                    sx={fieldSx}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select Class —</em></MenuItem>
                    {Array.isArray(classes) && classes.length > 0 ? (
                      classes.map((c) => (
                        <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No options available</em></MenuItem>
                    )}
                  </Select>
                  {mandatoryFieldsError && !registrationData.classId && (
                    <FormHelperText error>Please select class</FormHelperText>
                  )}
                </Grid>
              </Grid>
            </div>

            {/* ── Section 2: Family Details ── */}
            <div className="eq-section">
              <div className="eq-section-label">Family Details</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="parentAndGuardian">
                    Father / Guardian Name <span className="req">*</span>
                  </label>
                  <TextField id="parentAndGuardian" type="text" fullWidth size="small"
                    value={registrationData.parentAndGuardian}
                    onChange={(e) => setRegistrationData({ ...registrationData, parentAndGuardian: e.target.value })}
                    placeholder="Father or guardian's name"
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !registrationData.parentAndGuardian && (
                    <FormHelperText error>Please enter father/guardian name</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="motherName">Mother Name</label>
                  <TextField id="motherName" type="text" fullWidth size="small"
                    value={registrationData.motherName}
                    onChange={(e) => setRegistrationData({ ...registrationData, motherName: e.target.value })}
                    placeholder="Mother's name"
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </div>

            {/* ── Section 3: Contact & Address ── */}
            <div className="eq-section">
              <div className="eq-section-label">Contact &amp; Address</div>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="contactNo">
                    Contact No. <span className="req">*</span>
                  </label>
                  <TextField id="contactNo" type="text" fullWidth size="small"
                    value={registrationData.contactNo}
                    onChange={(e) => setRegistrationData({ ...registrationData, contactNo: e.target.value })}
                    inputProps={{ maxLength: 10 }}
                    placeholder="10-digit mobile number"
                    sx={fieldSx}
                  />
                  {mandatoryFieldsError && !registrationData.contactNo && (
                    <FormHelperText error>Please enter contact number</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="alternateMobile">Alternate Number</label>
                  <TextField id="alternateMobile" type="text" fullWidth size="small"
                    value={registrationData.alternateMobile}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => setRegistrationData({ ...registrationData, alternateMobile: e.target.value })}
                    placeholder="Secondary contact (optional)"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="emailId">Email ID</label>
                  <TextField id="emailId" type="email" fullWidth size="small"
                    value={registrationData.emailId}
                    onChange={(e) => setRegistrationData({ ...registrationData, emailId: e.target.value })}
                    placeholder="youremail@example.com"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="prospectus">Prospectus</label>
                  <TextField id="prospectus" type="text" fullWidth size="small"
                    value={registrationData.prospectus}
                    onChange={(e) => setRegistrationData({ ...registrationData, prospectus: e.target.value })}
                    placeholder="Prospectus details"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="aadharNo">Aadhar No.</label>
                  <TextField id="aadharNo" type="text" fullWidth size="small"
                    value={registrationData.aadharNo}
                    onChange={(e) => setRegistrationData({ ...registrationData, aadharNo: e.target.value })}
                    placeholder="12-digit Aadhar number"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label className="eq-label" htmlFor="address">Address</label>
                  <TextField id="address" type="text" fullWidth size="small" multiline rows={3}
                    value={registrationData.address}
                    onChange={(e) => setRegistrationData({ ...registrationData, address: e.target.value })}
                    placeholder="Full address"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <label className="eq-label" htmlFor="country">Country</label>
                  <Select id="country" fullWidth size="small" displayEmpty
                    value={registrationData.country}
                    onChange={(e) => {
                      setRegistrationData({ ...registrationData, country: e.target.value, state: "", city: "" });
                      fetchState(e.target.value);
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select Country —</em></MenuItem>
                    {Array.isArray(countries) && countries.length > 0 ? (
                      countries.map((c) => (
                        <MenuItem key={c.countryId} value={c.countryId}>{c.countryName}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No options available</em></MenuItem>
                    )}
                  </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <label className="eq-label" htmlFor="state">State</label>
                  <Select id="state" fullWidth size="small" displayEmpty
                    value={registrationData.state}
                    onChange={(e) => {
                      setRegistrationData({ ...registrationData, state: e.target.value, city: "" });
                      fetchCity(e.target.value);
                    }}
                    sx={fieldSx}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select State —</em></MenuItem>
                    {Array.isArray(states) && states.length > 0 ? (
                      states.map((s) => (
                        <MenuItem key={s.stateId} value={s.stateId}>{s.stateName}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No options available</em></MenuItem>
                    )}
                  </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <label className="eq-label" htmlFor="city">City</label>
                  <Select id="city" fullWidth size="small" displayEmpty
                    value={registrationData.city}
                    onChange={(e) => setRegistrationData({ ...registrationData, city: e.target.value })}
                    sx={fieldSx}
                  >
                    <MenuItem value=""><em style={{ color: '#aaa', fontStyle: 'normal' }}>— Select City —</em></MenuItem>
                    {Array.isArray(cities) && cities.length > 0 ? (
                      cities.map((c) => (
                        <MenuItem key={c.cityId} value={c.cityId}>{c.cityName}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled><em>No options available</em></MenuItem>
                    )}
                  </Select>
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="pinCode">Pin Code</label>
                  <TextField id="pinCode" type="text" fullWidth size="small"
                    value={registrationData.pinCode}
                    onChange={(e) => setRegistrationData({ ...registrationData, pinCode: e.target.value })}
                    placeholder="6-digit pin code"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <label className="eq-label" htmlFor="nationality">Nationality</label>
                  <TextField id="nationality" type="text" fullWidth size="small"
                    value={registrationData.nationality}
                    onChange={(e) => setRegistrationData({ ...registrationData, nationality: e.target.value })}
                    placeholder="Nationality"
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </div>

            {/* ── Footer strip ── */}
            <div className="eq-footer-strip">
              <span className="eq-footer-note">
                Fields marked <span style={{ color: 'var(--error)' }}>*</span> are required
              </span>
              <div className="eq-btn-row">
                <Button type="button" className="eq-btn-reset" onClick={handleReset} disableRipple>
                  Reset
                </Button>
                <Button type="submit" className="eq-btn-save" disableElevation disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit Registration"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Success Modal ── */}
      <Dialog open={showSuccessModal} onClose={() => setShowSuccessModal(false)}
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', maxWidth: 420 } }}>
        <Box sx={{ backgroundColor: '#0B1F40', padding: '14px 20px' }}>
          <Typography sx={{ color: '#fff', fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '18px' }}>
            Registration Submitted ✓
          </Typography>
        </Box>
        <Box sx={{ padding: '24px 24px 20px' }}>
          <Typography sx={{ mb: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: '#1A1714' }}>
            Thank you! Your registration has been received.
          </Typography>
          <Typography sx={{ mb: 3, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#7A7267' }}>
            Our admissions team will review your details and get in touch with you shortly.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
  onClick={() => {
    setShowSuccessModal(false);
    handleReset(); // Reset the form
    navigate(-1);  // Navigate back
  }}
  variant="contained"
  disableElevation
  sx={{
    background: '#0B1F40',
    borderRadius: '10px',
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 600,
    textTransform: 'none',
    px: 3,
    '&:hover': { background: '#132d5a' },
  }}
>
  Done
</Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Missing Fields Modal ── */}
      <Dialog open={mandatoryFieldsError} onClose={() => setMandatoryFieldsError(false)}
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', maxWidth: 400 } }}>
        <Box sx={{ backgroundColor: '#0B1F40', padding: '14px 20px' }}>
          <Typography sx={{ color: '#fff', fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '18px' }}>
            Required Fields Missing
          </Typography>
        </Box>
        <Box sx={{ padding: '24px 24px 20px' }}>
          <Typography sx={{ mb: 2, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#7A7267' }}>
            Please fill in the following required fields to continue:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 3, '& li': { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#C0392B', mb: .5 } }}>
            {missingFields.map((field, index) => <li key={index}>{field}</li>)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setMandatoryFieldsError(false)} variant="contained" disableElevation
              sx={{
                background: '#0B1F40', borderRadius: '10px',
                fontFamily: "'DM Sans',sans-serif", fontWeight: 600,
                textTransform: 'none', px: 3,
                '&:hover': { background: '#132d5a' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default StudentRegistration;